import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  levels: {
    debug: boolean;
    info: boolean;
    warn: boolean;
    error: boolean;
  };
}

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private config: LoggerConfig = {
    enabled: this.getDefaultEnabledState(),
    levels: {
      debug: true,
      info: true,
      warn: true,
      error: true,
    },
  };

  constructor() {
    // Make logger globally available for runtime control
    (window as any).schenkwerkLogger = {
      enable: () => this.enable(),
      disable: () => this.disable(),
      enableLevel: (level: LogLevel) => this.enableLevel(level),
      disableLevel: (level: LogLevel) => this.disableLevel(level),
      status: () => this.getStatus(),
      help: () => this.showHelp(),
    };

    // Show help message on startup if logger is enabled
    if (this.config.enabled) {
      this.showStartupMessage();
    }
  }

  /**
   * Determine default state based on environment
   * Enabled by default in development, disabled in production/staging
   */
  private getDefaultEnabledState(): boolean {
    // Check if there's a localStorage override
    const stored = localStorage.getItem('schenkwerk_debug_enabled');
    if (stored !== null) {
      return stored === 'true';
    }

    // Default based on environment
    return environment.env === 'dev';
  }

  /**
   * Enable all logging
   */
  enable(): void {
    this.config.enabled = true;
    localStorage.setItem('schenkwerk_debug_enabled', 'true');
    console.log('✅ [Logger] Debug logging ENABLED');
    this.showHelp();
  }

  /**
   * Disable all logging
   */
  disable(): void {
    this.config.enabled = false;
    localStorage.setItem('schenkwerk_debug_enabled', 'false');
    console.log('❌ [Logger] Debug logging DISABLED');
  }

  /**
   * Enable specific log level
   */
  enableLevel(level: LogLevel): void {
    this.config.levels[level] = true;
    console.log(`✅ [Logger] ${level.toUpperCase()} level enabled`);
  }

  /**
   * Disable specific log level
   */
  disableLevel(level: LogLevel): void {
    this.config.levels[level] = false;
    console.log(`❌ [Logger] ${level.toUpperCase()} level disabled`);
  }

  /**
   * Get current logger status
   */
  getStatus(): LoggerConfig {
    const status = {
      ...this.config,
      environment: environment.env,
    };
    console.log('📊 [Logger] Current Status:', status);
    return this.config;
  }

  /**
   * Show help message
   */
  private showHelp(): void {
    console.log(`
╔══════════════════════════════════════════════════════════════╗
║              Schenkwerk Logger - Control Panel               ║
╚══════════════════════════════════════════════════════════════╝

Available commands (via window.schenkwerkLogger):
  
  .enable()              - Enable all debug logging
  .disable()             - Disable all debug logging
  .enableLevel(level)    - Enable specific level (debug|info|warn|error)
  .disableLevel(level)   - Disable specific level
  .status()              - Show current logger status
  .help()                - Show this help message

Examples:
  window.schenkwerkLogger.enable()
  window.schenkwerkLogger.disableLevel('debug')
  window.schenkwerkLogger.status()
    `);
  }

  /**
   * Show startup message
   */
  private showStartupMessage(): void {
    console.log('🔧 [Logger] Debug logging is ENABLED');
    console.log('💡 [Logger] Type window.schenkwerkLogger.help() for control options');
  }

  /**
   * Main logging methods
   */

  log(...args: any[]): void {
    if (this.config.enabled && this.config.levels.debug) {
      console.log(...args);
    }
  }

  debug(...args: any[]): void {
    if (this.config.enabled && this.config.levels.debug) {
      console.debug(...args);
    }
  }

  info(...args: any[]): void {
    if (this.config.enabled && this.config.levels.info) {
      console.info(...args);
    }
  }

  warn(...args: any[]): void {
    if (this.config.enabled && this.config.levels.warn) {
      console.warn(...args);
    }
  }

  error(...args: any[]): void {
    // Errors are always shown regardless of config (but can be disabled with disableLevel)
    if (this.config.levels.error) {
      console.error(...args);
    }
  }

  /**
   * Group logging methods
   */

  group(label: string): void {
    if (this.config.enabled && this.config.levels.debug) {
      console.group(label);
    }
  }

  groupCollapsed(label: string): void {
    if (this.config.enabled && this.config.levels.debug) {
      console.groupCollapsed(label);
    }
  }

  groupEnd(): void {
    if (this.config.enabled && this.config.levels.debug) {
      console.groupEnd();
    }
  }

  /**
   * Table logging
   */

  table(data: any): void {
    if (this.config.enabled && this.config.levels.debug) {
      console.table(data);
    }
  }

  /**
   * Time tracking
   */

  time(label: string): void {
    if (this.config.enabled && this.config.levels.debug) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (this.config.enabled && this.config.levels.debug) {
      console.timeEnd(label);
    }
  }
}
